import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { FaChevronDown } from 'react-icons/fa';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import globalUserProfile from '../../../../stores/global-user-profile';
import { ProfessorProfile } from '../../../../api/types/settings/settings-all';
import { UserType } from '../../../../api/types/user/user-type';
import { DocDetails } from '../../../../api/types/docs/doc-details';
import { DocFeedbackStatusDB } from '../../../../api/types/docs/doc-feedback-status';
import { DocFeedbackItem } from '../../../../pages/user-page/pages/docs/componenets/doc-feedback-item';
import { AddNewDocDialog } from '../../../../pages/user-page/pages/docs/componenets/add-new-doc-dialog';
import { AddNewDocFeedback } from '../../../../pages/user-page/pages/docs/componenets/add-new-doc-feedback';
import dateFormat from '../../../../utils/date-format';

export const MyDocs = observer(() => {
  const stepperRef = useRef(null);
  const [displayAddDialog, setDisplayAddDialog] = useState(false);
  const [addFeedbackIndex, setAddFeedbackIndex] = useState<number | null>(null);

  // Get the professor's documents from the global user profile
  const getDocs = (): DocDetails[] => {
    if (!globalUserProfile.userProfile) return [];

    if (globalUserProfile.userProfile.userType === UserType.LECTURER) {
      const professorProfile = globalUserProfile.userProfile
        .profile as ProfessorProfile;
      return professorProfile.docs || [];
    }

    return [];
  };

  const [docs, setDocs] = useState<DocDetails[]>(getDocs());

  // Update docs when global user profile changes
  useEffect(() => {
    setDocs(getDocs());
  }, [globalUserProfile.userProfile]);

  // Get the current user summary for props that need it
  const getCurrentUser = () => {
    return globalUserProfile.userProfile?.userWithRole;
  };

  const currentUser = getCurrentUser();

  const getTag = (status: DocFeedbackStatusDB) => {
    switch (status) {
      case DocFeedbackStatusDB.Approved:
        return <Tag severity={'success'} value={'אושר'} />;
      case DocFeedbackStatusDB.Init:
        return <Tag severity={'warning'} value={'ממתין לתגובה'} />;
      case DocFeedbackStatusDB.Reject:
        return <Tag severity={'danger'} value={'נדחה, ממתין לתגובה'} />;
    }
  };

  if (!currentUser) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', width: '100%' }}>
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        המסמכים שלי
      </div>

      <Dialog
        header="יצירת דרישה למסמך"
        visible={displayAddDialog}
        onHide={() => setDisplayAddDialog(false)}
        style={{
          width: '500px',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <AddNewDocDialog
          programs={[]} // Professors might not need programs for docs
          userId={currentUser.id}
          user={currentUser}
          onHide={() => {
            globalUserProfile.getUserProfile();
            setDisplayAddDialog(false);
          }}
          updateState={false}
        />
      </Dialog>

      <Button
        label={'הוספת מסמך'}
        onClick={() => setDisplayAddDialog(true)}
        style={{ marginBottom: '20px' }}
      />

      {docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          לא נמצאו מסמכים
        </div>
      ) : (
        <Accordion
          style={{ direction: 'ltr', width: '100%' }}
          expandIcon={(options) => (
            <FaChevronDown
              style={{
                transform: options.isActive ? 'rotate(0deg)' : 'rotate(90deg)',
                transition: 'transform 0.3s',
                marginLeft: '8px',
              }}
            />
          )}
          collapseIcon={(options) => (
            <FaChevronDown
              style={{
                transform: options.isActive ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                marginLeft: '8px',
              }}
            />
          )}
        >
          {docs.map((docItem, docIndex) => (
            <AccordionTab
              key={`doc-${docItem.docMetadata.id}`}
              headerStyle={{ direction: 'rtl' }}
              style={{ direction: 'rtl' }}
              headerTemplate={() => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          direction: 'rtl',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {docItem.docMetadata.docName}
                        </div>
                        <div style={{ marginRight: '8px', color: '#666' }}>
                          {docItem.feedbacks.length
                            ? `(${docItem.feedbacks.length} תגובות)`
                            : 'אין תגובות'}
                        </div>
                      </div>
                      <div
                        style={{
                          fontWeight: 'normal',
                          fontSize: '14px',
                          color: '#888',
                        }}
                      >
                        נוצר ב{' '}
                        {dateFormat.displayFormatDate(
                          new Date(docItem.docMetadata.createdAt),
                        )}
                      </div>
                    </div>
                    {getTag(
                      docItem.feedbacks.length > 0
                        ? docItem.feedbacks[docItem.feedbacks.length - 1].status
                        : DocFeedbackStatusDB.Init,
                    )}
                  </div>
                );
              }}
              header={'document-header'} // Required prop, but overridden by headerTemplate
            >
              {docItem.feedbacks.map((feedBackItem, index) => (
                <DocFeedbackItem
                  key={`feedback-${feedBackItem.id || index}`}
                  feedback={feedBackItem}
                  userId={currentUser.id}
                  user={currentUser}
                  index={index + 1}
                  isLast={index === docItem.feedbacks.length - 1}
                  isRejectItem={false}
                  setDocs={setDocs}
                  isAdminModeActive={false}
                />
              ))}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'left',
                  marginTop: '10px',
                }}
              >
                <Button
                  label={'הוספת תגובה +'}
                  style={{
                    height: '30px',
                    display: 'flex',
                    justifyContent: 'left',
                  }}
                  severity={'secondary'}
                  outlined={true}
                  onClick={() => setAddFeedbackIndex(docIndex)}
                />
              </div>

              <Dialog
                header="הוספת תגובה למסמך"
                visible={addFeedbackIndex === docIndex}
                onHide={() => setAddFeedbackIndex(null)}
                style={{
                  width: '500px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                }}
              >
                <AddNewDocFeedback
                  userId={currentUser.id}
                  docId={docItem.docMetadata.id}
                />
              </Dialog>
            </AccordionTab>
          ))}
        </Accordion>
      )}
    </div>
  );
});
